import {
  Building2,
  Check,
  ChevronsUpDown,
  FileText,
  Gauge,
  Handshake,
  HeartPulse,
  Home,
  LineChart,
  Megaphone,
  Rocket,
  Settings,
  Trophy,
  Users,
} from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { useVisibleSections } from '@/app/layout/useVisibleSections';
import { Dropdown } from '@/app/components/ui/Dropdown';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { renderLabel } from '@/i18n/renderLabel';
import { cn } from '@/lib/cn';

/** One icon per main-menu section id — same ids across editions (§
 * Hoofdmenu), so this map works for both Fit and Soccer. Falls back to
 * `Home` for a section id that isn't listed (there is none today). Still
 * used for Soccer's grouped pills (`home`/`insights`/`programs`); Fit's
 * flat list uses `ITEM_ICONS` below instead. */
const SECTION_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  home: Home,
  insights: LineChart,
  programs: Rocket,
};

/** One icon per flat-list item id (§ Hoofdmenu redesign — Fit's sidebar is
 * a single flat list, each row with its own icon instead of one icon per
 * group). Falls back to `Home` for an id that isn't listed (Soccer's
 * grouped items never reach this map). */
const ITEM_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  overview: Gauge,
  vitality: HeartPulse,
  participation: Handshake,
  pilots: Rocket,
  challenges: Trophy,
  communications: Megaphone,
  documents: FileText,
  users: Users,
  reports: LineChart,
  settings: Settings,
  departments: Building2,
};

/** The normal app's `AppShell` top slot: the active company, switchable
 * when there is more than one to choose from (§ Bedrijfscontext) — no
 * leading icon, per the redesign. */
export function CompanySwitcher() {
  const { t } = useTranslation();
  const { company, companies, hasChoice, setActive } = useActiveCompany();
  if (!company) return null;

  if (!hasChoice) {
    return (
      <p className="text-ink truncate px-3 text-sm font-semibold" data-testid="sidebar-company">
        {company.name}
      </p>
    );
  }

  return (
    <Dropdown
      align="start"
      label={t('shell.activeCompany')}
      trigger={({ open }) => (
        <span
          className="text-ink hover:bg-mint-tint flex w-full items-center justify-between gap-2 rounded-2xl px-3 py-2 text-left text-sm font-semibold"
          data-testid="sidebar-company"
        >
          <span className="truncate">{company.name}</span>
          <ChevronsUpDown
            className={cn('text-accent size-4 shrink-0 transition-transform', open && 'rotate-180')}
            aria-hidden="true"
          />
        </span>
      )}
    >
      <ul className="m-0 flex w-56 max-w-[75vw] list-none flex-col p-0 text-sm">
        {companies.map((c) => {
          const isActive = company.id === c.id;
          return (
            <li key={c.id}>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => setActive(c.id)}
                className="hover:bg-mint-tint flex w-full items-center gap-2 rounded-2xl px-2 py-2 text-left"
              >
                <span className="w-4 shrink-0">
                  {isActive && <Check className="size-4" aria-hidden="true" />}
                </span>
                <span className="truncate">{c.name}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </Dropdown>
  );
}

/** The normal app's `AppShell` nav: every main-menu section with its
 * always-visible subitems — shared by the desktop aside and the mobile
 * drawer (via `AppShell`) so the two surfaces never disagree about what is
 * on offer. */
export function SidebarNav({ onNavigate }: { onNavigate?: (() => void) | undefined }) {
  const { t } = useTranslation();
  const sections = useVisibleSections();

  /** A row shared by both the flat-list and grouped-pill rendering below —
   * icon plus label, same link styling either way. */
  function itemRow(
    item: (typeof sections)[number]['visibleItems'][number],
    Icon: ComponentType<{ className?: string }>,
  ) {
    return (
      <li key={item.id}>
        <NavLink
          to={item.to}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'rounded-pill flex h-9 items-center gap-2.5 px-3 text-sm',
              'hover:bg-mint-tint transition-colors motion-reduce:transition-none',
              'focus-visible:ring-accent focus-visible:ring-offset-paper focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
              isActive ? 'bg-mint text-ink font-semibold' : 'text-ink',
            )
          }
        >
          <Icon className="size-4 shrink-0" aria-hidden="true" />
          {renderLabel(t, item.labelKey)}
        </NavLink>
      </li>
    );
  }

  // A section with exactly one item (Fit's flat list, § Hoofdmenu redesign)
  // contributes a plain row with its own icon — no group header. Runs of
  // these are merged into one continuous `<ul>` so the flat list reads as
  // one list, not one tiny group per item. A section with several items
  // (Soccer's grouped pills) still gets its own header-plus-icon block.
  const blocks: ReactNode[] = [];
  let flatRun: ReactNode[] = [];
  const flushFlatRun = () => {
    if (flatRun.length === 0) return;
    blocks.push(
      <ul key={`flat-${blocks.length}`} className="m-0 flex list-none flex-col gap-1 p-0">
        {flatRun}
      </ul>,
    );
    flatRun = [];
  };

  for (const section of sections) {
    if (section.visibleItems.length === 0) continue;

    if (section.visibleItems.length === 1) {
      const item = section.visibleItems[0]!;
      flatRun.push(itemRow(item, ITEM_ICONS[item.id] ?? Home));
      continue;
    }

    flushFlatRun();
    const SectionIcon = SECTION_ICONS[section.id] ?? Home;
    blocks.push(
      <div key={section.id}>
        <p className="text-mint mb-2 flex items-center gap-2 px-3 text-[11px] font-bold tracking-wider uppercase">
          <SectionIcon className="size-4 shrink-0" aria-hidden="true" />
          {renderLabel(t, section.labelKey)}
        </p>
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {section.visibleItems.map((item) => itemRow(item, ITEM_ICONS[item.id] ?? Home))}
        </ul>
      </div>,
    );
  }
  flushFlatRun();

  return (
    <nav aria-label={t('shell.mainNav')} className="flex flex-col gap-6">
      {blocks}
    </nav>
  );
}
